import { Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { getPublicFileUrl } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import ScrollReveal from "./ScrollReveal";
import { LANDING } from "@/constants/testIds";

const PLATFORMS = {
  instagram: { name: "Instagram", Icon: Instagram },
  facebook: { name: "Facebook", Icon: Facebook },
};

// Fix BD (2026-10-30): "Novedades": publicaciones de Instagram/Facebook que el equipo carga en Configuración
// (foto, texto y link a la publicación). Sin publicaciones activas, la sección no aparece.
export default function NewsSection({ posts, clinic }) {
  const { t } = usePublicLang();
  if (!posts || posts.length === 0) return null;

  const follow = [
    clinic.instagram_url && { key: "instagram", url: clinic.instagram_url },
    clinic.facebook_url && { key: "facebook", url: clinic.facebook_url },
  ].filter(Boolean);
  // Con 1 o 2 publicaciones la grilla se centra; desde 3, tres columnas en pantallas grandes.
  const gridCols = posts.length === 1 ? "grid-cols-1 max-w-sm mx-auto" : posts.length === 2 ? "grid-cols-2 max-w-3xl mx-auto" : "grid-cols-2 lg:grid-cols-3";

  return (
    <section id="novedades" className="py-20 md:py-28 px-5 bg-[#F3EDE4]" data-testid={LANDING.newsSection}>
      <div className="max-w-6xl mx-auto">
        <ScrollReveal className="text-center mb-12 md:mb-16">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-[#292522]">{t("landing.news.sectionTitle")}</h2>
          <p className="text-base text-[#6B6058] mt-3">{t("landing.news.subtitle")}</p>
        </ScrollReveal>

        <div className={`grid ${gridCols} gap-4 md:gap-8`}>
          {posts.map((post, idx) => {
            const platform = PLATFORMS[post.platform] || PLATFORMS.instagram;
            const { Icon } = platform;
            const card = (
              <>
                <div className="relative rounded-2xl overflow-hidden aspect-square bg-white border border-[rgba(185,154,98,0.25)]">
                  <img src={getPublicFileUrl(post.image_url)} alt={post.caption || platform.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center text-[var(--brand-primary)] shadow">
                    <Icon className="w-4 h-4" />
                  </span>
                </div>
                {post.caption && <p className="text-sm text-[#6B6058] leading-relaxed mt-3 line-clamp-3">{post.caption}</p>}
                {post.link_url && (
                  <span className="inline-flex items-center gap-1 mt-2 text-xs uppercase tracking-[0.14em] font-medium text-[#292522] border-b border-[var(--brand-primary)] pb-0.5">
                    {t("landing.news.viewOn", { platform: platform.name })} <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                )}
              </>
            );
            return (
              <ScrollReveal key={post.id} delay={(idx % 3) * 0.08}>
                {post.link_url ? (
                  <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="group block" data-testid={`${LANDING.newsCardPrefix}${post.id}`}>
                    {card}
                  </a>
                ) : (
                  <div className="group" data-testid={`${LANDING.newsCardPrefix}${post.id}`}>{card}</div>
                )}
              </ScrollReveal>
            );
          })}
        </div>

        {follow.length > 0 && (
          <ScrollReveal className="flex flex-wrap justify-center gap-3 mt-12">
            {follow.map(({ key, url }) => {
              const { name, Icon } = PLATFORMS[key];
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-[var(--brand-primary)] text-[#292522] px-5 py-2.5 rounded-full text-sm font-medium bg-white/60 hover:bg-white transition-colors"
                  data-testid={`${LANDING.newsFollowPrefix}${key}`}
                >
                  <Icon className="w-4 h-4 text-[var(--brand-primary)]" /> {t("landing.news.follow", { platform: name })}
                </a>
              );
            })}
          </ScrollReveal>
        )}
      </div>
    </section>
  );
}
